"use client";

import backgroundOverlay from "@/app/(public)/shared/assets/background-overlay2.png";
import { motion } from "motion/react";
import { AlertTriangle } from "lucide-react";
import Link from "next/link";
import { useEffect, useRef } from "react";
import Logo from "@/components/ui/Logo";
import type { AdminEntryGateStep } from "../_logic/admin-org-context";
import { AdminContextBanner } from "./AdminContextBanner";

/** Label shown only on the org step. The owner step never shows a counter (see the component doc). */
const ORG_STEP_LABEL = "Step 1 of 2";

/** Id linking the stale-org notice to the heading's `aria-describedby`, see the focus-management note below. */
const STALE_NOTICE_ID = "admin-entry-gate-stale-notice";

interface AdminEntryGateProps {
  /** Which pre-wizard picker step this render hosts. */
  step: AdminEntryGateStep;
  /** True when `admin_org_id` was present but failed to resolve (KTD3). Shown only on the org
   *  step, distinguishing a stale/invalid link from a launch that was never scoped. Ignored when
   *  `step` is `"owner"`. */
  isOrgParamInvalid?: boolean;
  /** Where "Back to admin dashboard" goes: the admin-dash page the launch came from, already
   *  validated by `resolveAdminReturnPath` (which falls back to the dashboard root). */
  backHref: string;
  /** The step's own picker content. A placeholder until the org-picker/owner-picker components
   *  land (separate units, built independently); kept as a slot so those units can drop their
   *  component in from `page.tsx` without touching this shell. */
  children: React.ReactNode;
}

/**
 * Shell a Nowadays admin sees at `/create` before `CreateEventWizard` mounts: the org-picker step
 * (skipped when already scoped, e.g. from admin-dash's "Create event" button; see
 * `resolveAdminOrgParamResolution`) and the owner-picker step (always required; see
 * `resolveAdminCreateEntryDecision`). Each step is its own server-resolved page load driven by URL
 * params rather than in-memory wizard state, so there's no session state to tell "org was already
 * scoped when this admin arrived" apart from "org was just picked in the step before this one" —
 * both look identical by the time the owner step renders. The "Step X of Y" label (KTD14) is scoped
 * to that constraint: it only ever appears on the org step ("Step 1 of 2"); the owner step never
 * shows a counter.
 *
 * @param props.step - Which picker this render hosts.
 * @param props.isOrgParamInvalid - Whether a stale/invalid `admin_org_id` should show its
 *   distinguishing notice (KTD3). Only meaningful when `step` is `"org"`.
 * @param props.backHref - The back link's destination.
 * @param props.children - The step's own picker content.
 * @returns The gate shell wrapping `children`.
 */
export const AdminEntryGate = ({ step, isOrgParamInvalid = false, backHref, children }: AdminEntryGateProps) => {
  // Derived values
  const showStaleNotice = step === "org" && isOrgParamInvalid;

  // Effects
  // Content present at initial mount is not reliably announced by a live region (screen readers
  // only pick up on changes to content already in the DOM), and the picker's own search input
  // autofocuses on mount — without this, focus jumps straight past the notice below. Focusing the
  // heading here runs in this component's own mount effect, which fires AFTER the child picker's
  // (child effects run before parent effects on mount), so it wins the race; `aria-describedby`
  // then has a screen reader read the notice right after the heading's own accessible name.
  const headingRef = useRef<HTMLHeadingElement>(null);
  useEffect(() => {
    if (showStaleNotice) headingRef.current?.focus();
  }, [showStaleNotice]);

  // Render
  return (
    <div className="min-h-screen w-screen bg-landing-primary relative overflow-y-auto flex flex-col items-center px-6 py-10">
      {/* Same background overlay texture as CreateEventWizard, so the entry gate matches the wizard
          it hands off to instead of looking like a different, flatter surface. */}
      <div
        className="absolute inset-0 pointer-events-none z-0"
        style={{
          backgroundImage: `url(${backgroundOverlay.src})`,
          backgroundSize: "100% auto",
          backgroundPosition: "center top",
          backgroundRepeat: "no-repeat",
          opacity: 0.2,
        }}
      />

      <div className="absolute top-4 left-4 md:top-6 md:left-8 z-10">
        <Logo size="md" priority />
      </div>

      <motion.div
        initial={{ opacity: 0, y: -6 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.4, ease: "easeOut" }}
        className="relative z-10 w-full max-w-[560px] flex flex-col gap-6 m-auto rounded-4xl bg-white p-8 shadow-2xl ring-1 ring-inset ring-gray-100"
      >
        {step === "org" && <span className="text-xs font-medium text-gray-500">{ORG_STEP_LABEL}</span>}

        <div className="flex flex-col gap-1">
          <h1
            ref={headingRef}
            tabIndex={-1}
            aria-describedby={showStaleNotice ? STALE_NOTICE_ID : undefined}
            className="text-lg font-semibold text-gray-900 tracking-tight outline-none"
          >
            {step === "org" ? "Choose an organization" : "Choose the event owner"}
          </h1>
          <p className="text-sm font-medium text-gray-600">
            {step === "org"
              ? "Pick the organization this event belongs to before continuing."
              : "Pick the user this event should belong to before continuing."}
          </p>
        </div>

        {showStaleNotice && (
          <AdminContextBanner
            id={STALE_NOTICE_ID}
            align="start"
            icon={<AlertTriangle className="h-4 w-4" strokeWidth={2} aria-hidden="true" />}
          >
            Couldn&apos;t find that organization. Pick one below.
          </AdminContextBanner>
        )}

        {children}

        <Link
          href={backHref}
          className="inline-flex min-h-[44px] -mx-2 w-fit items-center self-start px-2 text-xs font-medium text-gray-500 transition-colors hover:text-gray-900"
        >
          Back to admin dashboard
        </Link>
      </motion.div>
    </div>
  );
};
